import { registerCommand, setFlagOverrides } from '../commands';
import { DEFAULT_LEFT_TAGS, DEFAULT_RIGHT_TAGS, type OverlayTag, type OverlayTagType, setOverlayTags } from '../overlay';
import type { ViewerShell } from '../shell';
import { messageDialog, openDialog, paragraph } from '../ui/dialogs';
import { setSeriesGrouping } from '../ui/studybrowser';
import { setSliceReorientation } from '../display/reorient';
import * as toolbar from '../ui/toolbar';
import type { ViewerPreferences } from './client';
import { risClient } from './session';

/**
 * User preferences (spec app-config-dialog, viewer contract §5): the "Configuración" dialog with its
 * Visualización and Etiqueta propiedades tabs, saved as the `visualization` and `labels` modules of the
 * ris preferences, and "Reiniciar Config". Read once at boot (2 s budget, defaults on failure).
 */

export const PREFERENCES_TEXT = {
  title: 'Configuración',
  saved: 'Configuración guardada.',
  saveError: 'No fue posible guardar la configuración.',
  reset: 'Configuración restablecida.',
  resetError: 'No fue posible restablecer la configuración.',
  nothingToReset: 'No existen datos de preferencias de usuario por eliminar.',
  badTag: (tag: string) => `Dicom Tag inválido: "${tag}" (use el formato 0010,0010).`,
} as const;

const LOAD_TIMEOUT_MS = 2000;
const TAG_PATTERN = /^\(?\s*([0-9a-f]{4})\s*,?\s*([0-9a-f]{4})\s*\)?$/i;

export interface VisualizationPreferences {
  sliceReorientation: boolean;
  flipToolsInTopMenu: boolean;
  splitMfSeries: boolean;
  groupSingleFrame: boolean;
}

export interface LabelPreferences {
  left: OverlayTag[];
  right: OverlayTag[];
}

/** IMEXHS defaults (spec section 7: sliceReorientation false, flip tools in More, split + group on). */
const VISUALIZATION_DEFAULTS: VisualizationPreferences = {
  sliceReorientation: false,
  flipToolsInTopMenu: false,
  splitMfSeries: true,
  groupSingleFrame: true,
};

const VISUALIZATION_OPTIONS: Array<{ key: keyof VisualizationPreferences; label: string; title: string }> = [
  { key: 'sliceReorientation', label: 'Reorientación de los cortes: ', title: 'Rotar la imagen según su orientación' },
  { key: 'flipToolsInTopMenu', label: 'Herramientas de espejo en el menú principal: ', title: 'Establecer las opciones de volteo en el menú' },
  { key: 'splitMfSeries', label: 'Dividir series multi-frame: ', title: 'Serie de multi-frame dividida en el panel' },
  { key: 'groupSingleFrame', label: 'Agrupar instancias con una sola imagen: ', title: 'Agrupar instancias de un solo fotograma en una serie' },
];

/** Tipo column: the four IMEXHS types; a row of another type (the default date + time) keeps its own. */
const TAG_TYPES: Array<{ value: OverlayTagType; label: string }> = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'age_string', label: 'Age String' },
  { value: 'date', label: 'Date' },
];
const EXTRA_TYPE_LABELS: Partial<Record<OverlayTagType, string>> = { date_and_time: 'Date Time', float: 'Number (2 dec.)' };
const KNOWN_TYPES = new Set<string>(['string', 'number', 'float', 'date', 'date_and_time', 'age_string']);

let current: ViewerPreferences | null = null;

// --- reading and applying ----------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function visualizationOf(prefs: ViewerPreferences | null): VisualizationPreferences {
  const stored = prefs?.modules.visualization;
  const out = { ...VISUALIZATION_DEFAULTS };
  if (isRecord(stored)) {
    for (const key of Object.keys(out) as Array<keyof VisualizationPreferences>) {
      if (typeof stored[key] === 'boolean') out[key] = stored[key] as boolean;
    }
  }
  return out;
}

function tagsOf(value: unknown, fallback: readonly OverlayTag[]): OverlayTag[] {
  if (!Array.isArray(value)) return fallback.map((tag) => ({ ...tag }));
  const tags: OverlayTag[] = [];
  for (const row of value) {
    if (!isRecord(row) || typeof row.tag !== 'string') continue;
    const type = typeof row.type === 'string' && KNOWN_TYPES.has(row.type) ? (row.type as OverlayTagType) : 'string';
    const tag: OverlayTag = { label: typeof row.label === 'string' ? row.label : '', tag: row.tag.toUpperCase(), type };
    if (typeof row.timeTag === 'string') tag.timeTag = row.timeTag.toUpperCase();
    tags.push(tag);
  }
  return tags;
}

export function labelsOf(prefs: ViewerPreferences | null): LabelPreferences {
  const stored = prefs?.modules.labels;
  const labels = isRecord(stored) ? stored : {};
  return { left: tagsOf(labels.left, DEFAULT_LEFT_TAGS), right: tagsOf(labels.right, DEFAULT_RIGHT_TAGS) };
}

/** Pushes the preferences into the modules that read them (toolbar gates, study browser, overlay). */
function apply(prefs: ViewerPreferences | null): void {
  const visualization = visualizationOf(prefs);
  setFlagOverrides(prefs?.modules.visualization ? { flipToolsInTopMenu: visualization.flipToolsInTopMenu } : {});
  setSeriesGrouping({ splitMfSeries: visualization.splitMfSeries, groupSingleFrame: visualization.groupSingleFrame });
  if (prefs?.modules.visualization) setSliceReorientation(visualization.sliceReorientation);
  const labels = labelsOf(prefs);
  setOverlayTags(labels.left, labels.right);
}

/** Reads the preferences at boot (before the feature modules build) and applies them; defaults on failure. */
export async function loadPreferences(): Promise<void> {
  const client = risClient();
  if (!client) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), LOAD_TIMEOUT_MS);
  });
  try {
    current = await Promise.race([client.preferences(), timeout]);
  } catch (error) {
    console.warn('[viewer] preferences unavailable, using defaults', error);
    current = null;
  } finally {
    clearTimeout(timer);
  }
  apply(current);
}

/** Rebuilds what depends on the preferences: the toolbar (flip tools), the browser tiles, the grid. */
function reloadView(shell: ViewerShell, previous: VisualizationPreferences): void {
  const next = visualizationOf(current);
  if (next.flipToolsInTopMenu !== previous.flipToolsInTopMenu) toolbar.install(shell);
  if (next.splitMfSeries !== previous.splitMfSeries || next.groupSingleFrame !== previous.groupSingleFrame) {
    shell.emit('studies-changed', { studies: shell.studies() });
  }
}

// --- Configuración dialog ------------------------------------------------------------------------

/** "0010,0010" / "(0010,0010)" / "00100010" → "00100010"; null when not a tag. */
export function normalizeTag(text: string): string | null {
  const match = TAG_PATTERN.exec(text.trim());
  return match ? `${match[1]}${match[2]}`.toUpperCase() : null;
}

function displayTag(tag: string): string {
  return /^[0-9A-F]{8}$/i.test(tag) ? `${tag.slice(0, 4)},${tag.slice(4)}` : tag;
}

interface TagRowState {
  row: HTMLTableRowElement;
  alias: HTMLInputElement;
  tag: HTMLInputElement;
  type: HTMLSelectElement;
  timeTag?: string;
}

function tagRow(body: HTMLTableSectionElement, rows: TagRowState[], tag: OverlayTag): void {
  const row = body.insertRow();
  row.className = 'config-tag-row';
  const alias = document.createElement('input');
  alias.type = 'text';
  alias.className = 'config-tag-alias';
  alias.value = tag.label;
  alias.maxLength = 40;
  alias.setAttribute('aria-label', 'Alias');
  const tagInput = document.createElement('input');
  tagInput.type = 'text';
  tagInput.className = 'config-tag-tag';
  tagInput.value = displayTag(tag.tag);
  tagInput.placeholder = '0010,0010';
  tagInput.setAttribute('aria-label', 'Dicom Tag');
  const type = document.createElement('select');
  type.className = 'config-tag-type';
  type.setAttribute('aria-label', 'Tipo');
  const options = [...TAG_TYPES];
  const extra = EXTRA_TYPE_LABELS[tag.type];
  if (extra && !options.some((o) => o.value === tag.type)) options.push({ value: tag.type, label: extra });
  for (const option of options) {
    const el = document.createElement('option');
    el.value = option.value;
    el.textContent = option.label;
    type.appendChild(el);
  }
  type.value = tag.type;
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'cmd-dialog-button config-tag-remove';
  remove.textContent = 'Quitar';
  const state: TagRowState = { row, alias, tag: tagInput, type, timeTag: tag.timeTag };
  remove.addEventListener('click', () => {
    rows.splice(rows.indexOf(state), 1);
    row.remove();
  });
  for (const el of [alias, tagInput, type, remove]) row.insertCell().appendChild(el);
  rows.push(state);
}

function tagTable(parent: HTMLElement, caption: string, side: 'left' | 'right', tags: OverlayTag[]): TagRowState[] {
  const rows: TagRowState[] = [];
  const section = document.createElement('div');
  section.className = `config-tags config-tags-${side}`;
  paragraph(section, caption, 'config-tags-caption');
  const table = document.createElement('table');
  table.className = 'config-tag-table';
  const head = table.createTHead().insertRow();
  for (const label of ['Alias', 'Dicom Tag', 'Tipo', '']) {
    const th = document.createElement('th');
    th.textContent = label;
    head.appendChild(th);
  }
  const body = table.createTBody();
  tags.forEach((tag) => tagRow(body, rows, tag));
  const add = document.createElement('button');
  add.type = 'button';
  add.className = 'cmd-dialog-button config-tag-add';
  add.textContent = 'Añadir etiqueta';
  add.addEventListener('click', () => {
    tagRow(body, rows, { label: '', tag: '', type: 'string' });
    rows[rows.length - 1].alias.focus();
  });
  section.append(table, add);
  parent.appendChild(section);
  return rows;
}

/** Reads the rows back; returns the first invalid tag text instead when there is one. */
function readRows(rows: TagRowState[]): OverlayTag[] | string {
  const tags: OverlayTag[] = [];
  for (const state of rows) {
    const text = state.tag.value;
    if (!text.trim() && !state.alias.value.trim()) continue;
    const tag = normalizeTag(text);
    if (!tag) return text;
    const out: OverlayTag = { label: state.alias.value.trim(), tag, type: state.type.value as OverlayTagType };
    if (state.timeTag && out.type === 'date_and_time') out.timeTag = state.timeTag;
    tags.push(out);
  }
  return tags;
}

function tabs(body: HTMLElement): { visualization: HTMLElement; labels: HTMLElement } {
  const bar = document.createElement('div');
  bar.className = 'config-tabs';
  bar.setAttribute('role', 'tablist');
  const panels: HTMLElement[] = [];
  const buttons: HTMLButtonElement[] = [];
  const select = (index: number) => {
    buttons.forEach((b, i) => {
      b.classList.toggle('active', i === index);
      b.setAttribute('aria-selected', String(i === index));
    });
    panels.forEach((p, i) => { p.hidden = i !== index; });
  };
  ['Visualización', 'Etiqueta propiedades'].forEach((label, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'config-tab';
    button.setAttribute('role', 'tab');
    button.textContent = label;
    button.addEventListener('click', () => select(index));
    bar.appendChild(button);
    buttons.push(button);
    const panel = document.createElement('div');
    panel.className = `config-panel config-panel-${index === 0 ? 'visualization' : 'labels'}`;
    panel.setAttribute('role', 'tabpanel');
    panels.push(panel);
  });
  body.append(bar, ...panels);
  select(0);
  return { visualization: panels[0], labels: panels[1] };
}

/** "Configuración" (app-config-dialog): Visualización / Etiqueta propiedades, Cancelar / Aplicar. */
export function openSettingsDialog(shell: ViewerShell): void {
  const visualization = visualizationOf(current);
  const labels = labelsOf(current);
  const checks = new Map<keyof VisualizationPreferences, HTMLInputElement>();
  let leftRows: TagRowState[] = [];
  let rightRows: TagRowState[] = [];
  let error: HTMLParagraphElement | null = null;
  const { body } = openDialog(shell, {
    title: PREFERENCES_TEXT.title,
    className: 'config-dialog',
    buttons: [
      { label: 'Cancelar', onClick: (close) => close() },
      {
        label: 'Aplicar',
        primary: true,
        onClick: (close) => {
          const left = readRows(leftRows);
          const right = readRows(rightRows);
          if (typeof left === 'string' || typeof right === 'string') {
            error ??= paragraph(body, '', 'config-error');
            error.textContent = PREFERENCES_TEXT.badTag(typeof left === 'string' ? left : String(right));
            return;
          }
          const nextVisualization = { ...visualization };
          checks.forEach((input, key) => { nextVisualization[key] = input.checked; });
          close();
          void savePreferences(shell, { visualization: { ...nextVisualization }, labels: { left, right } });
        },
      },
    ],
  });
  const panels = tabs(body);
  for (const option of VISUALIZATION_OPTIONS) {
    const label = document.createElement('label');
    label.className = `config-option config-${option.key}`;
    label.title = option.title;
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = visualization[option.key];
    input.dataset.key = option.key;
    checks.set(option.key, input);
    label.append(document.createTextNode(option.label), input);
    panels.visualization.appendChild(label);
  }
  leftRows = tagTable(panels.labels, 'Información - lado izquierdo:', 'left', labels.left);
  rightRows = tagTable(panels.labels, 'Información - lado derecho:', 'right', labels.right);
}

async function savePreferences(shell: ViewerShell, modules: { visualization: VisualizationPreferences; labels: LabelPreferences }): Promise<void> {
  const client = risClient();
  if (!client) return;
  const previous = visualizationOf(current);
  const base: ViewerPreferences = current ? { ...current } : { modules: {} };
  delete base.customized;
  const body: ViewerPreferences = {
    ...base,
    modules: {
      ...base.modules,
      visualization: { ...modules.visualization },
      labels: { left: modules.labels.left.map((t) => ({ ...t })), right: modules.labels.right.map((t) => ({ ...t })) },
    },
  };
  try {
    current = await client.savePreferences(body);
  } catch (error) {
    console.warn('[viewer] preferences save failed', error);
    shell.toast(PREFERENCES_TEXT.saveError, 'error');
    return;
  }
  apply(current);
  reloadView(shell, previous);
  shell.toast(PREFERENCES_TEXT.saved);
}

/** "Reiniciar Config": back to the defaults, then rebuild the view with them. */
async function resetPreferences(shell: ViewerShell): Promise<void> {
  const client = risClient();
  if (!client) return;
  if (current && current.customized === false) {
    messageDialog(shell, PREFERENCES_TEXT.title, PREFERENCES_TEXT.nothingToReset, 'config-reset-dialog', 'CERRAR');
    return;
  }
  const previous = visualizationOf(current);
  try {
    current = await client.resetPreferences();
  } catch (error) {
    console.warn('[viewer] preferences reset failed', error);
    shell.toast(PREFERENCES_TEXT.resetError, 'error');
    return;
  }
  apply(current);
  reloadView(shell, previous);
  shell.setLayout(shell.layout());
  shell.toast(PREFERENCES_TEXT.reset);
}

interface PreferencesDebug {
  current(): ViewerPreferences | null;
}

declare global {
  interface Window {
    __viewerPreferences?: PreferencesDebug;
  }
}

export function install(shell: ViewerShell): void {
  if (!risClient()) return;
  registerCommand('settings', () => openSettingsDialog(shell));
  registerCommand('reset-config', () => void resetPreferences(shell));
  window.__viewerPreferences = { current: () => current };
}
